import { Fragment, type FunctionComponent } from 'react';
import {
  ArrowRightMd,
  Link,
  typographyHelper,
  paletteHelper,
  spacingHelper,
  HomeMd,
  mediaHelper,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { PageContainer } from '../PageContainer/PageContainer';
import { clickThroughSourceTracking } from '../../analytics/click-through-tracking/clickThroughSourceTracking';
import { PcaLink } from '../../app-kit/components/PcaLink/PcaLink';
import { WebLink } from '../../app-kit/components/WebLink/types';

const BreadCrumbsContainer = styled.ol`
  list-style: none;
  display: flex;
  align-items: center;
  color: ${paletteHelper('textSecondary')};
  padding: ${spacingHelper('small')} 0;
  white-space: nowrap;
  overflow: auto;
`;

const StyledLink = styled(Link).attrs<{ $isCurrentPage: boolean }>(({ $isCurrentPage }) => {
  if ($isCurrentPage) {
    return { 'aria-current': 'page' };
  }
  return {};
})`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
`;

const StyledListItem = styled.li`
  display: flex;
  align-items: center;
  &:last-of-type {
    ${StyledLink} {
      color: ${paletteHelper('textSecondary')};
    }
  }
  &:not(:last-of-type):hover {
    ${StyledLink} {
      color: ${paletteHelper('textPrimary')};
    }
  }
`;

const StyledArrowRightMd = styled(ArrowRightMd)`
  height: 10px;
  width: 10px;
  margin: 0 ${spacingHelper('extraSmall')};
`;

const StyledHomeMd = styled(HomeMd)`
  height: 12px;
  width: 12px;
`;

export interface BreadCrumb {
  title: string;
  url: string;
}

interface BreadCrumbsProps {
  crumbs: BreadCrumb[] | WebLink[];
}

const isWebLink = (crumb: BreadCrumb | WebLink): crumb is WebLink => {
  return (crumb as WebLink).tracking !== undefined;
};

export const BreadCrumbs: FunctionComponent<BreadCrumbsProps> = ({ crumbs }) => (
  <nav aria-label="breadcrumbs">
    <BreadCrumbsContainer itemScope itemType="https://schema.org/BreadcrumbList">
      {crumbs.map((crumb, index) => {
        const { title, url } = crumb;
        const tracking = isWebLink(crumb) ? crumb.tracking : null;
        const isLastCrumb = crumbs.length - 1 === index;
        const isHomeCrumb = url === '/';

        return (
          <Fragment key={`${title}${url}`}>
            <StyledListItem
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              <StyledLink
                forwardedAs={PcaLink}
                itemProp="item"
                to={url}
                variant="primary"
                title={title}
                clientSideRouting={false}
                $isCurrentPage={isLastCrumb}
                icon={isHomeCrumb ? <StyledHomeMd /> : null}
                disabled={isLastCrumb}
                onClick={() => clickThroughSourceTracking(tracking?.clickThroughTracking ?? null)}
              >
                <span itemProp="name">{title}</span>
              </StyledLink>
              <meta itemProp="position" content={`${index + 1}`} />
              {!isLastCrumb && <StyledArrowRightMd data-testid="breadcrumb-arrow-divider" />}
            </StyledListItem>
          </Fragment>
        );
      })}
    </BreadCrumbsContainer>
  </nav>
);

const BreadCrumbsBarContainer = styled.div`
  ${mediaHelper('tabletMax')} {
    padding-left: ${spacingHelper('large')};
    padding-right: ${spacingHelper('large')};
  }
  border-color: ${paletteHelper('borderSecondary')};
  border-style: solid;
  border-width: 1px 0;
  border-top: none;
`;

export const BreadCrumbsBar: FunctionComponent<BreadCrumbsProps> = ({ crumbs }) => {
  return (
    <BreadCrumbsBarContainer>
      <PageContainer>
        <BreadCrumbs crumbs={crumbs} />
      </PageContainer>
    </BreadCrumbsBarContainer>
  );
};
